import { action, observable, computed, createAtom, makeObservable } from 'mobx'
import { uistore } from '../../uistore'
import toJS from '../../../utils/tojs'
import * as TYPE_HANDLERS from './field-handlers'
import getPath from 'lodash/get'
import { refCache } from './record-cache'
export const RECORD_SYMBOL = Symbol('Record')

class Meta {
  instance = observable.map({}, { deep: false })

  constructor (record, dataSource, fields, Record) {
    makeObservable(this, {
      update: action,
      snapshot: computed
    })

    this.Record = Record
    this.record = record
    this.dataSource = dataSource
    this.dataSourceId = dataSource && dataSource.config.id
    this.fields = fields
    this.atom = createAtom('Record ' + this.dataSourceId)
  }

  update (obj = {}) {
    for (const k in obj) {
      if (k === '$') continue
      this.record[k] = obj[k]
    }
    this.atom.reportChanged()
  }

  get snapshot () {
    this.atom.reportObserved()
    const s = { ...toJS(this.record), ...toJS(this.instance) }
    for (const k in s) {
      if (s[k] === undefined) delete s[k]
    }
    delete s.$
    return s
  }

  toValueLabel = (key, _obj, referenceData = null, value = null) => {
    const overrideToValueLabel = getPath(_obj, '$.dataSource.config.toValueLabel')
    if (overrideToValueLabel) return overrideToValueLabel(key, _obj, referenceData, value)

    const parts = key.split('.')
    const last = parts.pop()
    let obj = _obj || this.record
    if (parts.length) {
      obj = getPath(obj, parts)
    }
    const _ref = getPath(obj, last)
    if (obj && obj.$ && _ref && obj.$.fields) {
      const refs = Array.prototype.concat(_ref).filter(x => x)
      const f = obj.$.fields.find(ff => ff.fieldPath === last)
      if (!f) return
      const ard = Array.prototype.concat(f.referenceData).filter(x => x)
      const res = []
      for (const ref of refs) {
        let rd
        if (ref && ref.$ && ref.$.dataSourceId) {
          rd = ard.find(r => r.dataSourceId === ref.$.dataSourceId)
        } else if (ard.length === 1) {
          rd = ard[0]
        }
        if (rd) {
          if (rd.resolvePath && obj[rd.resolvePath] && typeof obj[rd.resolvePath] === 'object') {
            res.push({
              value: obj[rd.resolvePath][rd.value],
              label: obj[rd.resolvePath][rd.label]
            })
          } else {
            res.push({ value: ref[rd.value], label: ref[rd.label] })
          }
        }
        if (f.type === 'REFERENCE' || f.type === 'POLYREFERENCE') return res[0]
      }
      if (res.length) return res
    }
  }

  clone = (updateObj) => {
    const cloned = new this.Record(this.snapshot, true)
    if (updateObj) Object.assign(cloned, updateObj)
    return cloned
  }
}

function updateRecord (record, item) {
  if (!item || !typeof item === 'object') return

  for (const key in toJS(item)) {
    if (key === '$') continue
    try {
      record[key] = item[key]
    } catch (e) {
      console.error(e)
    }
  }
  record.$?.atom?.reportChanged()
}

const _refsNeeded = computed(() => uistore.dataSourceFactory.app.refsNeeded || {})

export const getRecordClass = (mapping, extensions) => {
  let dataSource =
      typeof mapping.recordType === 'string' &&
      uistore?.dataSourceFactory?.getInstance(mapping.recordType)
  if (!dataSource && mapping.dataSource) dataSource = uistore.dataSourceFactory.getInstance(mapping.dataSource.id)
  const fields = mapping.recordFields || []
  const refsNeeded = (dataSource && _refsNeeded.get()[dataSource.config.id]) || []
  let hasRefs = false
  class Record {
    constructor (item, clone = false) {
      if (!clone) {
        let existing

        for (const idField of refsNeeded) {
          const record = refCache.get(dataSource.config.id, idField, item[idField])
          if (record) {
            existing = record
          } else {
            refCache.add(dataSource.config.id, idField, item[idField], existing || this)
          }
        }

        if (existing) {
          updateRecord(existing, item)
          return existing
        }
      }

      Object.defineProperty(this, '$', {
        enumerable: false,
        writable: false,
        configurable: false,
        value: new Meta(this, dataSource, fields, Record)
      })
      updateRecord(this, item)

      this[RECORD_SYMBOL] = true
      if (hasRefs) {
        makeObservable(this)
      }
    }
  }

  const handler = getFieldHandler(extensions)

  const obj = Record.prototype

  for (const c of fields || []) {
    const key = c.fieldPath
    const refKey = (c.referenceData && c.referenceData.resolvePath) || key

    if (key && key.length) {
      const keyHandler = handler(c, key)

      if (!keyHandler) {
        const k = key.split('.')[0]
        obj[k] = undefined
        continue
      }

      Object.defineProperty(obj, refKey, {
        enumerable: true,
        configurable: true,
        get: keyHandler.get,
        set: keyHandler.set
      })

      computed(obj, refKey)
      hasRefs = true

      if (key !== refKey) {
        const { get, set } = TYPE_HANDLERS.identity(c, key)
        Object.defineProperty(obj, key, {
          enumerable: true,
          configurable: true,
          get,
          set
        })
        computed(obj, key)
      }
    }
  }

  return Record
}

const getFieldHandler = (extensions) => (field, key) => {
  let def
  const handler = TYPE_HANDLERS[field.type]
  if (typeof handler === 'function') def = handler(field, key)
  if (extensions && typeof extensions[field.type] === 'function') { def = extensions[field.type](field, key, def) }
  return def
}

export default function toRecords (mapping, data, clone = false) {
  if (!data || !Array.isArray(data) || !mapping) return
  const Record = getRecordClass(mapping)
  return data.map(d => new Record(d, clone))
}
